import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

// Approx lat/lng of major Indian cities (extend freely; unknown cities are listed below the map)
const CITY_COORDS = {
  mumbai: [19.076, 72.8777], pune: [18.5204, 73.8567], bengaluru: [12.9716, 77.5946],
  bangalore: [12.9716, 77.5946], thane: [19.2183, 72.9781], delhi: [28.6139, 77.209], "new delhi": [28.6139, 77.209],
  gurugram: [28.4595, 77.0266], gurgaon: [28.4595, 77.0266], noida: [28.5355, 77.391],
  hyderabad: [17.385, 78.4867], chennai: [13.0827, 80.2707], kolkata: [22.5726, 88.3639],
  ahmedabad: [23.0225, 72.5714], surat: [21.1702, 72.8311], vadodara: [22.3072, 73.1812],
  jaipur: [26.9124, 75.7873], lucknow: [26.8467, 80.9462], kanpur: [26.4499, 80.3319],
  nagpur: [21.1458, 79.0882], indore: [22.7196, 75.8577], bhopal: [23.2599, 77.4126],
  patna: [25.5941, 85.1376], chandigarh: [30.7333, 76.7794], kochi: [9.9312, 76.2673],
  cochin: [9.9312, 76.2673], coimbatore: [11.0168, 76.9558], visakhapatnam: [17.6868, 83.2185],
  vijayawada: [16.5062, 80.648], thiruvananthapuram: [8.5241, 76.9366], guwahati: [26.1445, 91.7362],
  bhubaneswar: [20.2961, 85.8245], ranchi: [23.3441, 85.3096], raipur: [21.2514, 81.6296],
  dehradun: [30.3165, 78.0322], amritsar: [31.634, 74.8723], ludhiana: [30.901, 75.8573],
  jodhpur: [26.2389, 73.0243], udaipur: [24.5854, 73.7125], nashik: [19.9975, 73.7898],
  aurangabad: [19.8762, 75.3433], rajkot: [22.3039, 70.8022], madurai: [9.9252, 78.1198],
  mysuru: [12.2958, 76.6394], mysore: [12.2958, 76.6394], mangaluru: [12.9141, 74.856],
  goa: [15.4909, 73.8278], panaji: [15.4909, 73.8278], shimla: [31.1048, 77.1734],
  srinagar: [34.0837, 74.7973], jammu: [32.7266, 74.857], varanasi: [25.3176, 82.9739],
  agra: [27.1767, 78.0081], allahabad: [25.4358, 81.8463], prayagraj: [25.4358, 81.8463],
  gwalior: [26.2183, 78.1828], jabalpur: [23.1815, 79.9864], siliguri: [26.7271, 88.3953],
  faridabad: [28.4089, 77.3178], ghaziabad: [28.6692, 77.4538], meerut: [28.9845, 77.7064],
  jamshedpur: [22.8046, 86.2029], dhanbad: [23.7957, 86.4304], asansol: [23.6739, 86.9524],
  tiruchirappalli: [10.7905, 78.7047], salem: [11.6643, 78.146], pondicherry: [11.9416, 79.8083],
  puducherry: [11.9416, 79.8083], guntur: [16.3067, 80.4365], warangal: [17.9689, 79.5941],
};

const norm = (s = "") => s.trim().toLowerCase();

const DEFAULT_CITIES = ["Mumbai", "Thane", "Pune", "Bangalore"];

export function IndiaPartnerMap() {
  const [geo, setGeo] = useState(null);
  const [data, setData] = useState({ cities: [], total_cities: 0, states: [] });

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL || ""}/india-map.json`).then((r) => r.json()).then(setGeo).catch(() => {});
    api.get("/public/partner-cities").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const proj = useMemo(() => {
    if (!geo) return null;
    const [minLng, minLat, maxLng, maxLat] = geo.bbox;
    const W = 1000;
    const midLat = (minLat + maxLat) / 2;
    const kx = Math.cos((midLat * Math.PI) / 180);
    const factor = W / ((maxLng - minLng) * kx);
    const H = (maxLat - minLat) * factor;
    const project = (lng, lat) => [(lng - minLng) * kx * factor, (maxLat - lat) * factor];
    return { W, H, project };
  }, [geo]);

  const paths = useMemo(() => {
    if (!geo || !proj) return [];
    const out = [];
    geo.features.forEach((f) => {
      f.polys.forEach((poly) => {
        let d = "";
        poly.forEach((ring) => {
          ring.forEach(([lng, lat], i) => {
            const [x, y] = proj.project(lng, lat);
            d += (i === 0 ? "M" : "L") + x.toFixed(1) + " " + y.toFixed(1);
          });
          d += "Z";
        });
        out.push(d);
      });
    });
    return out;
  }, [geo, proj]);

  const activePins = useMemo(() => {
    if (!proj) return [];
    const byLoc = new Map();
    const add = (city, count) => {
      const c = CITY_COORDS[norm(city)];
      if (!c) return;
      const key = c[0] + "," + c[1];
      if (byLoc.has(key)) {
        byLoc.get(key).count += count;
      } else {
        const [x, y] = proj.project(c[1], c[0]);
        byLoc.set(key, { city, count, x, y });
      }
    };
    DEFAULT_CITIES.forEach((c) => add(c, 0));
    data.cities.forEach(({ city, count }) => add(city, count));
    return Array.from(byLoc.values());
  }, [data, proj]);

  const unmapped = data.cities.filter((c) => !CITY_COORDS[norm(c.city)]).map((c) => c.city);

  return (
    <div className="grid md:grid-cols-2 gap-8 items-center" data-testid="india-partner-map">
      <div className="relative">
        {proj && (
          <svg viewBox={`0 0 ${proj.W} ${proj.H}`} className="w-full h-auto drop-shadow-sm" role="img" aria-label="Fixigo partner coverage across India">
            {paths.map((d, i) => (
              <path key={i} d={d} fill="#E2E8F0" stroke="#ffffff" strokeWidth="1.2" className="transition-colors" />
            ))}
            {activePins.map((p, i) => (
              <g key={p.city} data-testid={`map-pin-${p.city.replace(/ /g, "-")}`}>
                <circle cx={p.x} cy={p.y} r="22" fill="#4F46E5" opacity="0.18">
                  <animate attributeName="r" values="14;30;14" dur="2.4s" begin={`${i * 0.25}s`} repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.28;0;0.28" dur="2.4s" begin={`${i * 0.25}s`} repeatCount="indefinite" />
                </circle>
                <circle cx={p.x} cy={p.y} r="9" fill="#4F46E5" stroke="#fff" strokeWidth="2.5" />
              </g>
            ))}
          </svg>
        )}
        {!proj && <div className="aspect-[1/1] rounded-2xl bg-[#F1F5F9] animate-pulse" />}
      </div>

      <div>
        <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#4F46E5]">Growing across India</p>
        <h2 className="font-[Outfit] text-3xl md:text-4xl font-bold tracking-tight mt-2">
          Now live in <span className="text-[#4F46E5]">{activePins.length}</span> {activePins.length === 1 ? "city" : "cities"}
        </h2>
        <p className="mt-3 text-[#475569] max-w-md leading-relaxed">
          Verified Fixigo professionals at your doorstep across India — and our network keeps growing in more cities every week.
        </p>

        {activePins.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6" data-testid="map-city-chips">
            {activePins.map((p) => (
              <span key={p.city} className="inline-flex items-center gap-1.5 bg-[#4F46E5]/10 text-[#4338CA] text-sm font-semibold px-3 py-1.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-[#4F46E5]" /> {p.city}
                {p.count > 1 && <span className="text-[#4F46E5]/70 font-bold">· {p.count}</span>}
              </span>
            ))}
          </div>
        )}
        {unmapped.length > 0 && (
          <p className="text-sm text-[#475569] mt-3">+ also serving {unmapped.join(", ")}</p>
        )}
      </div>
    </div>
  );
}
